/**
 * Print-only rendering of Daily Photo List sheets.
 *
 * Hidden on screen via CSS class "printable-sheets" (display: none in @media screen).
 * When printing, this becomes the only visible content via @media print rules
 * in print.css. Each sheet starts on a new page with repeating column headers.
 *
 * Reuses the same column definitions and cell styling as PhotoListTable.
 */
import React from "react"

import type { PhotoListVehicle } from "../../api/photoList"
import {
  HAND_ADDED_NOTES_MARK,
  getColumnsForSheet,
  type PhotoListColumn,
} from "../../config/photoListColumns"
import { PHOTO_LIST_SHEETS } from "../../context/PhotoListContext"
import { getCellStyle } from "../../utils/photoListStyling"
import { THEME } from "../../config/theme"

interface Props {
  vehiclesBySheet: Record<string, PhotoListVehicle[]>
  selectedSheets: string[]
  sheetOrder: string[]
  runDate: string | null
}

/** Map display label → slug for column lookup */
const LABEL_TO_SLUG: Record<string, string> = {}
for (const s of PHOTO_LIST_SHEETS) {
  LABEL_TO_SLUG[s.label] = s.slug
}

function formatTitleDate(isoDate: string | null): string {
  if (!isoDate) return ""
  const d = new Date(isoDate)
  return d.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    timeZone: "America/Los_Angeles",
  })
}

/** Map PhotoListVehicle field key → print CSS class name */
const KEY_TO_COL_CLASS: Record<string, string> = {
  age_days: "col-age",
  sale_status_raw: "col-status",
  recon_status_raw: "col-rapid-recon",
  cmpgn: "col-stop",
  transit: "col-transit",
  vin: "col-vin",
  stock: "col-stock",
  stargard: "col-stargard",
  shot_status: "col-shot-status",
  year: "col-year",
  make: "col-make",
  model: "col-model",
  trim: "col-trim",
  color_ext: "col-color",
  print_notes: "col-notes",
}

function formatCellValue(col: PhotoListColumn, value: unknown): string {
  if (value === null || value === undefined || value === "") return "\u2014"
  if (col.key === "age_days" && typeof value === "number") {
    return value.toLocaleString()
  }
  return String(value)
}

export default function PrintableSheets({
  vehiclesBySheet,
  selectedSheets,
  sheetOrder,
  runDate,
}: Props) {
  const orderedSheets = sheetOrder.filter((label) => selectedSheets.includes(label))

  return (
    <div className="printable-sheets">
      {orderedSheets.map((label) => {
        const slug = LABEL_TO_SLUG[label]
        if (!slug) return null
        const vehicles = vehiclesBySheet[label] ?? []
        const columns = getColumnsForSheet(slug)

        return (
          <div key={label} className="printable-sheet">
            <img
              className="printable-sheet-lockup"
              src="/lockup-on-light.png"
              alt="INVI Auto"
            />
            <table
              style={{
                borderCollapse: "collapse",
                width: "100%",
                fontSize: 9,
                fontFamily: THEME.fonts.primary,
              }}
            >
              {/* Column headers repeat on every printed page */}
              <thead>
                {/* Title row */}
                <tr>
                  <th
                    colSpan={columns.length}
                    style={{
                      backgroundColor: THEME.auditTable.titleBarBackground,
                      color: THEME.auditTable.titleBarText,
                      padding: "4px 8px",
                      fontSize: 12,
                      fontWeight: 600,
                      textAlign: "left",
                    }}
                  >
                    {label}
                    {runDate && (
                      <span style={{ fontWeight: 400, marginLeft: 10, fontSize: 10 }}>
                        {formatTitleDate(runDate)}
                      </span>
                    )}
                    <span style={{ float: "right", fontWeight: 400, fontSize: 9 }}>
                      {vehicles.length.toLocaleString()} vehicle{vehicles.length !== 1 ? "s" : ""}
                    </span>
                  </th>
                </tr>

                {/* Column headers — explicit white + caption styling so the
                    navy headerBackground is readable and matches the on-screen
                    photo list header. */}
                <tr>
                  {columns.map((col) => (
                    <th
                      key={col.key}
                      className={KEY_TO_COL_CLASS[col.key] ?? ""}
                      style={{
                        backgroundColor: THEME.auditTable.headerBackground,
                        color: THEME.colors.white,
                        padding: "3px 4px",
                        fontWeight: 500,
                        fontSize: 8,
                        letterSpacing: "0.5px",
                        textTransform: "uppercase",
                        textAlign: col.align ?? "left",
                        borderBottom: `1px solid ${THEME.auditTable.borderColor}`,
                        borderRight: "1px solid rgba(255, 255, 255, 0.1)",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {col.header}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {vehicles.map((vehicle, rowIdx) => {
                  const zebraColor =
                    rowIdx % 2 === 0
                      ? THEME.auditTable.zebraLight
                      : THEME.auditTable.zebraMain
                  return (
                    <tr
                      key={vehicle.id}
                      style={{ backgroundColor: zebraColor }}
                    >
                      {columns.map((col) => {
                        const raw = vehicle[col.key]
                        // Notes stays blank for pens, except a hand-added
                        // car reads ADDED, as in the Excel (Ian, 2026-10-11).
                        const display =
                          col.key === "print_notes" && vehicle.added_by_hand
                            ? HAND_ADDED_NOTES_MARK
                            : formatCellValue(col, raw)
                        const cellStyle = getCellStyle(vehicle, col.key)
                        return (
                          <td
                            key={col.key}
                            className={KEY_TO_COL_CLASS[col.key] ?? ""}
                            style={{
                              padding: "2px 4px",
                              borderRight: `1px solid ${THEME.auditTable.borderColor}`,
                              textAlign: col.align ?? "left",
                              fontFamily: col.mono ? THEME.fonts.mono : undefined,
                              fontSize: col.mono ? 8 : undefined,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              ...cellStyle,
                            }}
                          >
                            {display}
                          </td>
                        )
                      })}
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )
      })}
    </div>
  )
}
